import { nanoid } from 'nanoid';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Notification = {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  duration?: number;
};

type NotificationsStore = {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  dismissNotification: (id: string) => void;
};

export const useNotifications = create<NotificationsStore>(set => ({
  notifications: [],
  addNotification: notification =>
    set(state => ({
      notifications: [
        ...state.notifications,
        { ...notification, id: nanoid() },
      ],
    })),
  dismissNotification: id =>
    set(state => ({
      notifications: state.notifications.filter(
        notification => notification.id !== id
      ),
    })),
}));

// WhatsNew/Notifications Drawer state
export type ActiveNotification = string | null;

interface NotificationsDrawerState {
  openSchoolCurriculum: boolean;
  openNaplan: boolean;
  openIMO: boolean;
  isDrawerOpen: boolean;
  activeNotification: ActiveNotification;
  seenNotificationIds: string[];
  setOpenSchoolCurriculum: (open: boolean) => void;
  setOpenNaplan: (open: boolean) => void;
  setOpenIMO: (open: boolean) => void;
  setIsDrawerOpen: (open: boolean) => void;
  setActiveNotification: (notification: ActiveNotification) => void;
  markNotificationsAsSeen: (notificationIds: string[]) => void;
}

export const useNotificationsDrawerStore = create<NotificationsDrawerState>()(
  persist(
    set => ({
      openSchoolCurriculum: false,
      openNaplan: false,
      openIMO: false,
      isDrawerOpen: false,
      activeNotification: null,
      seenNotificationIds: [],
      setOpenSchoolCurriculum: (_open: boolean) =>
        set({ openSchoolCurriculum: _open }),
      setOpenIMO: (_open: boolean) => set({ openIMO: _open }),
      setOpenNaplan: (_open: boolean) => set({ openNaplan: _open }),
      setIsDrawerOpen: (open: boolean) => set({ isDrawerOpen: open }),
      setActiveNotification: (notification: ActiveNotification) =>
        set({ activeNotification: notification }),
      markNotificationsAsSeen: (notificationIds: string[]) =>
        set(state => ({
          seenNotificationIds: [
            ...new Set([...state.seenNotificationIds, ...notificationIds]),
          ],
        })),
    }),
    {
      name: 'notifications-drawer-storage',
      partialize: state => ({ seenNotificationIds: state.seenNotificationIds }),
    }
  )
);

/**
 * Helper function to check if all current notifications have been seen
 */
export const hasSeenNotifications = (
  seenIds: string[],
  currentIds: string[]
): boolean => {
  if (currentIds.length === 0) return true;
  return currentIds.every(id => seenIds.includes(id));
};
